import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Plus, Check, Loader, ChevronDown } from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { floorsApi, departmentsApi, employeesApi, teamsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';

function AddFloor() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [floorName, setFloorName] = useState('');

  // Floor Head
  const [selectedDepartmentHead, setSelectedDepartmentHead] = useState('');
  const [selectedDepartmentHeadId, setSelectedDepartmentHeadId] = useState(null);
  const [selectedEmployeeHead, setSelectedEmployeeHead] = useState(null);

  // Floor Manager
  const [selectedDepartmentManager, setSelectedDepartmentManager] = useState('');
  const [selectedDepartmentManagerId, setSelectedDepartmentManagerId] = useState(null);
  const [selectedEmployeeManager, setSelectedEmployeeManager] = useState(null);

  // Teams and Agents
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState({});
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [teamEmployees, setTeamEmployees] = useState({});
  const [loadingTeamEmployees, setLoadingTeamEmployees] = useState({});

  // Dropdown states
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(null);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [agentSearchTerm, setAgentSearchTerm] = useState('');

  // Data
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [depts, emps, tms] = await Promise.all([
        departmentsApi.getAllDepartments(),
        employeesApi.getAllEmployees(),
        teamsApi.getAllTeams()
      ]);

      console.log('Departments:', depts);
      console.log('Employees:', emps);
      console.log('Teams:', tms);

      setDepartments(depts);
      setEmployees(emps);
      setTeams(tms);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch employees for a specific team
  const fetchEmployeesByTeam = async (teamId) => {
    try {
      setLoadingTeamEmployees(prev => ({ ...prev, [teamId]: true }));

      console.log('Fetching employees for team:', teamId);
      console.log('All employees:', employees);

      // Try API first
      try {
        const data = await employeesApi.getEmployeesByTeam(teamId);
        console.log('API Response:', data);

        // Handle different response structures
        let employeesList = [];
        if (Array.isArray(data)) {
          employeesList = data;
        } else if (data?.employees && Array.isArray(data.employees)) {
          employeesList = data.employees;
        } else if (data?.data && Array.isArray(data.data)) {
          employeesList = data.data;
        }

        console.log('Processed employees from API:', employeesList);

        if (employeesList.length > 0) {
          setTeamEmployees(prev => ({
            ...prev,
            [teamId]: employeesList
          }));
          return employeesList;
        }
      } catch (apiError) {
        console.log('API failed, using fallback filter method:', apiError.message);
      }

      // Fallback: Filter from all employees
      const teamEmployeesList = employees.filter(emp => {
        // Check different possible field names
        const empTeamId = emp.teamId || emp.team_id || emp.team?.id || emp.teamID;
        console.log(`Employee ${emp.name} (${emp.id}) - teamId: ${empTeamId}, looking for: ${teamId}`);

        // Use == for loose comparison (handles string vs number)
        return empTeamId == teamId;
      });

      console.log('Filtered team employees:', teamEmployeesList);

      setTeamEmployees(prev => ({
        ...prev,
        [teamId]: teamEmployeesList
      }));

      return teamEmployeesList;

    } catch (error) {
      console.error('Error fetching employees:', error);
      setTeamEmployees(prev => ({
        ...prev,
        [teamId]: []
      }));
      return [];
    } finally {
      setLoadingTeamEmployees(prev => ({ ...prev, [teamId]: false }));
    }
  };

  const handleToggleTeam = (teamId) => {
    if (selectedTeams.includes(teamId)) {
      // Remove team
      setSelectedTeams(selectedTeams.filter(id => id !== teamId));
      const newAgents = { ...selectedAgents };
      delete newAgents[teamId];
      setSelectedAgents(newAgents);
      // Close dropdown if this team was expanded
      if (expandedTeam === teamId) {
        setExpandedTeam(null);
      }
    } else {
      // Add team
      setSelectedTeams([...selectedTeams, teamId]);
    }
  };

  const handleToggleAgent = (teamId, agentId) => {
    const teamAgents = selectedAgents[teamId] || [];
    if (teamAgents.includes(agentId)) {
      setSelectedAgents({
        ...selectedAgents,
        [teamId]: teamAgents.filter(id => id !== agentId)
      });
    } else {
      setSelectedAgents({
        ...selectedAgents,
        [teamId]: [...teamAgents, agentId]
      });
    }
  };

  // Toggle agent dropdown for a team
  const handleToggleAgentDropdown = async (teamId, e) => {
    e.stopPropagation();

    // If opening dropdown and employees not loaded yet, fetch them
    if (expandedTeam !== teamId && !teamEmployees[teamId]) {
      await fetchEmployeesByTeam(teamId);
    }

    setExpandedTeam(expandedTeam === teamId ? null : teamId);
    setAgentSearchTerm(''); // Reset search when opening
  };

  const handleSave = async () => {
    // Validation
    if (!floorName.trim()) {
      message.error('Please enter floor name');
      return;
    }

    if (!selectedEmployeeHead) {
      message.error('Please select floor head');
      return;
    }

    if (!selectedEmployeeManager) {
      message.error('Please select floor manager');
      return;
    }

    if (selectedTeams.length === 0) {
      message.error('Please select at least one team');
      return;
    }

    try {
      setLoading(true);

      // Format teams with their agents properly
      const teamsWithAgents = selectedTeams.map(teamId => ({
        teamId: teamId,
        agentIds: selectedAgents[teamId] || []
      }));

      const floorData = {
        name: floorName,
        floorHeadDepartmentId: selectedDepartmentHeadId,
        floorHeadEmployeeId: selectedEmployeeHead.id,
        floorManagerDepartmentId: selectedDepartmentManagerId,
        floorManagerEmployeeId: selectedEmployeeManager.id,
        teamIds: selectedTeams
      };
        
      console.log('Creating floor with data:', JSON.stringify(floorData, null, 2));

      const response = await floorsApi.createFloor(floorData);
      console.log('Floor created successfully:', response);

      setShowSuccessToast(true);
      setTimeout(() => {
        navigate('/floor');
      }, 2000);
    } catch (error) {
      console.error('Error creating floor:', error);
      console.error('Error response:', error.response?.data);

      // Better error handling
      let errorMessage = 'Unknown error occurred';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error('Failed to create floor: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employeeId.includes(searchTerm)
  );

  // Get agents for a team with search filter
  const getTeamAgents = (teamId) => {
    const teamEmps = teamEmployees[teamId] || [];

    if (!agentSearchTerm.trim()) {
      return teamEmps;
    }

    return teamEmps.filter(emp =>
      emp.name.toLowerCase().includes(agentSearchTerm.toLowerCase()) ||
      (emp.employeeId && emp.employeeId.toLowerCase().includes(agentSearchTerm.toLowerCase()))
    );
  };

  return (
    <div className="space-y-6">
      {/* Success Toast */}
      {showSuccessToast && (
        <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 rounded-lg px-6 py-3 flex items-center space-x-3 shadow-lg z-50 animate-fade-in border-2 ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white'}`} style={{ backgroundColor: isDark ? undefined : `${currentTheme.color}15`, borderColor: isDark ? undefined : `${currentTheme.color}30` }}>
          <span className={`font-bold ${isDark ? 'text-white' : ''}`} style={{ color: isDark ? undefined : currentTheme.color }}>Floor added successfully!</span>
          <button onClick={() => setShowSuccessToast(false)} style={{ color: currentTheme.color }} className="hover:opacity-70 transition-opacity">
            ✕
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/floor')}
          className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
        >
          <ArrowLeft className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-600'}`} />
        </button>
        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Add new floor</h2>
      </div>

      {/* Floor Details Card */}
      <div className={`rounded-lg p-6 border transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Floor details</h3>

        <div className="space-y-6">
          {/* Floor Name */}
          <div>
            <label className={`text-sm mb-2 block font-bold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Floor name</label>
            <input
              type="text"
              placeholder="Collection"
              value={floorName}
              onChange={(e) => setFloorName(e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300'}`}
              style={{ borderColor: floorName ? currentTheme.color : undefined }}
            />
          </div>

          {/* Floor Head and Manager Selection */}
          <div className="grid grid-cols-2 gap-6">
            {/* Select Floor Head */}
            <div className="space-y-4">
              <label className={`text-sm block font-bold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Select floor head</label>
              <div className="grid grid-cols-2 gap-3">
                {/* Department Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowDepartmentDropdown(showDepartmentDropdown === 'head' ? null : 'head')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none transition-all text-left flex items-center justify-between ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                  >
                    <span className="font-medium">{selectedDepartmentHead || 'Select Department'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showDepartmentDropdown === 'head' ? 'rotate-180' : ''}`} />
                  </button>

                  {showDepartmentDropdown === 'head' && (
                    <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl z-20 max-h-60 overflow-y-auto border animate-fadeIn ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'}`}>
                      {departments.map((dept) => (
                        <button
                          key={dept.id}
                          onClick={() => {
                            setSelectedDepartmentHead(dept.name);
                            setSelectedDepartmentHeadId(dept.id);
                            setShowDepartmentDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'}`}
                        >
                          {dept.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Employee Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowEmployeeDropdown(showEmployeeDropdown === 'head' ? null : 'head')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none transition-all text-left flex items-center justify-between ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                  >
                    <span className="font-medium">Select Employee</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showEmployeeDropdown === 'head' ? 'rotate-180' : ''}`} />
                  </button>

                  {showEmployeeDropdown === 'head' && (
                    <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl z-20 max-h-80 overflow-y-auto border animate-fadeIn ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'}`}>
                      <div className={`p-3 border-b sticky top-0 transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
                        <ExpandableSearchBar
                          searchTerm={searchTerm}
                          onSearchChange={setSearchTerm}
                          placeholder="Search"
                          fullWidth
                          className="w-full"
                        />
                      </div>
                      {filteredEmployees.map((emp) => (
                        <button
                          key={emp.id}
                          onClick={() => {
                            setSelectedEmployeeHead(emp);
                            setShowEmployeeDropdown(null);
                            setSearchTerm('');
                          }}
                          className={`w-full px-4 py-3 text-left transition-colors flex items-center space-x-3 ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                            selectedEmployeeHead?.id === emp.id 
                              ? (isDark ? 'bg-white/20' : 'bg-teal-100') 
                              : (isDark ? 'bg-white/5' : 'bg-gray-100')
                            }`}>
                            {selectedEmployeeHead?.id === emp.id ? (
                              <Check className="w-5 h-5" style={{ color: currentTheme.color }} />
                            ) : (
                              <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-600'}`}>{emp.name.charAt(0)}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{emp.name}</p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Emp: {emp.employeeId}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Employee Head */}
              {selectedEmployeeHead && (
                <div className={`flex items-center space-x-2 border rounded-lg px-3 py-2 w-fit transition-all ${isDark ? 'bg-white/10 border-white/10' : ''}`} style={{ backgroundColor: isDark ? undefined : `${currentTheme.color}15`, borderColor: isDark ? undefined : `${currentTheme.color}30` }}>
                  <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedEmployeeHead.name}</span>
                  <button
                    onClick={() => setSelectedEmployeeHead(null)}
                    className={`ml-2 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Select Floor Manager */}
            <div className="space-y-4">
              <label className={`text-sm block font-bold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Select floor Manager</label>
              <div className="grid grid-cols-2 gap-3">
                {/* Department Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowDepartmentDropdown(showDepartmentDropdown === 'manager' ? null : 'manager')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none transition-all text-left flex items-center justify-between ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                  >
                    <span className="font-medium">{selectedDepartmentManager || 'Select Department'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showDepartmentDropdown === 'manager' ? 'rotate-180' : ''}`} />
                  </button>

                  {showDepartmentDropdown === 'manager' && (
                    <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl z-20 max-h-60 overflow-y-auto border animate-fadeIn ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'}`}>
                      {departments.map((dept) => (
                        <button
                          key={dept.id}
                          onClick={() => {
                            setSelectedDepartmentManager(dept.name);
                            setSelectedDepartmentManagerId(dept.id);
                            setShowDepartmentDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'}`}
                        >
                          {dept.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Employee Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowEmployeeDropdown(showEmployeeDropdown === 'manager' ? null : 'manager')}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none transition-all text-left flex items-center justify-between ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                  >
                    <span className="font-medium">Select Employee</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showEmployeeDropdown === 'manager' ? 'rotate-180' : ''}`} />
                  </button>

                  {showEmployeeDropdown === 'manager' && (
                    <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl z-20 max-h-80 overflow-y-auto border animate-fadeIn ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'}`}>
                      <div className={`p-3 border-b sticky top-0 transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
                        <ExpandableSearchBar
                          searchTerm={searchTerm}
                          onSearchChange={setSearchTerm}
                          placeholder="Search"
                          fullWidth
                          className="w-full"
                        />
                      </div>
                      {filteredEmployees.map((emp) => (
                        <button
                          key={emp.id}
                          onClick={() => {
                            setSelectedEmployeeManager(emp);
                            setShowEmployeeDropdown(null);
                            setSearchTerm('');
                          }}
                          className={`w-full px-4 py-3 text-left transition-colors flex items-center space-x-3 ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                            selectedEmployeeManager?.id === emp.id 
                              ? (isDark ? 'bg-white/20' : 'bg-teal-100') 
                              : (isDark ? 'bg-white/5' : 'bg-gray-100')
                            }`}>
                            {selectedEmployeeManager?.id === emp.id ? (
                              <Check className="w-5 h-5" style={{ color: currentTheme.color }} />
                            ) : (
                              <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-600'}`}>{emp.name.charAt(0)}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{emp.name}</p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Emp: {emp.employeeId}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Employee Manager */}
              {selectedEmployeeManager && (
                <div className={`flex items-center space-x-2 border rounded-lg px-3 py-2 w-fit transition-all ${isDark ? 'bg-white/10 border-white/10' : ''}`} style={{ backgroundColor: isDark ? undefined : `${currentTheme.color}15`, borderColor: isDark ? undefined : `${currentTheme.color}30` }}>
                  <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedEmployeeManager.name}</span>
                  <button
                    onClick={() => setSelectedEmployeeManager(null)}
                    className={`ml-2 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Teams & Agents Card */}
      <div className={`rounded-lg p-6 border transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Teams & Agents</h3>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 h-[600px]">
          {/* Left Side: Teams List */}
          <div className={`md:col-span-12 ${expandedTeam ? 'lg:col-span-5 border-r border-white/5' : 'lg:col-span-12'} pr-4 overflow-y-auto custom-scrollbar transition-all duration-300`}>
            <h4 className={`text-xs font-bold uppercase tracking-wider mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>TEAMS</h4>
            <div className={`space-y-3 ${!expandedTeam ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 space-y-0' : ''}`}>
              {teams.map((team) => (
                <div
                  key={team.id}
                  onClick={(e) => handleToggleAgentDropdown(team.id, e)}
                  className={`w-full px-4 py-4 border-2 rounded-lg transition-all cursor-pointer flex items-center justify-between group ${
                    expandedTeam === team.id
                    ? 'shadow-sm'
                    : isDark ? 'bg-white/5 border-white/5 hover:bg-white/10' : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  style={{
                    borderColor: expandedTeam === team.id ? currentTheme.color : undefined,
                    backgroundColor: expandedTeam === team.id ? `${currentTheme.color}10` : undefined
                  }}
                >
                  <div className="flex items-center space-x-3">
                    {/* Checkbox for team selection */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleTeam(team.id); }}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors`}
                      style={{
                        borderColor: selectedTeams.includes(team.id) ? currentTheme.color : '#D1D5DB',
                        backgroundColor: selectedTeams.includes(team.id) ? currentTheme.color : 'transparent'
                      }}
                    >
                      {selectedTeams.includes(team.id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </button>
                    <div>
                      <span className={`font-bold block transition-colors ${isDark ? 'text-white' : ''}`}>{team.name}</span>
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedAgents[team.id]?.length || 0} agents selected</span>
                    </div>
                  </div>

                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expandedTeam === team.id ? 'rotate-270' : '-rotate-90'}`} style={{ color: expandedTeam === team.id ? currentTheme.color : undefined }} />
                </div>
              ))}
            </div>
          </div>

          {/* Right Side: Agents Selection */}
          <div className={`md:col-span-12 lg:col-span-7 pl-2 flex flex-col h-full transition-all ${expandedTeam ? 'block animate-fade-in' : 'hidden'}`}>
            {expandedTeam && (
              <>
                <div className={`mb-4 pb-4 border-b ${isDark ? 'border-white/5' : ''}`}>
                  <h4 className={`text-xs font-bold uppercase tracking-wider mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>AGENTS IN {teams.find(t => t.id === expandedTeam)?.name}</h4>

                  {/* Search Agents */}
                  <div className="mb-4">
                    <ExpandableSearchBar
                      searchTerm={agentSearchTerm}
                      onSearchChange={setAgentSearchTerm}
                      placeholder="Search agents by name or ID..."
                      fullWidth
                      className={`w-full transition-all ${isDark ? 'bg-white/5 border-white/5 focus-within:bg-white/10' : 'bg-gray-50 focus-within:bg-white'}`}
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                  {loadingTeamEmployees[expandedTeam] ? (
                    <div className="flex flex-col items-center justify-center h-48 space-y-3">
                      <Loader className="w-8 h-8 animate-spin" style={{ color: currentTheme.color }} />
                      <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Loading agents...</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                      {getTeamAgents(expandedTeam).length > 0 ? (
                        getTeamAgents(expandedTeam).map((agent) => (
                          <div
                            key={agent.id}
                            onClick={() => handleToggleAgent(expandedTeam, agent.id)}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-start space-x-3`}
                            style={{
                              borderColor: selectedAgents[expandedTeam]?.includes(agent.id) ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6'),
                              backgroundColor: selectedAgents[expandedTeam]?.includes(agent.id) ? (isDark ? 'rgba(255,255,255,0.05)' : `${currentTheme.color}10`) : (isDark ? 'rgba(255,255,255,0.02)' : 'white')
                            }}
                          >
                            <div className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors`}
                              style={{
                                borderColor: selectedAgents[expandedTeam]?.includes(agent.id) ? currentTheme.color : '#D1D5DB',
                                backgroundColor: selectedAgents[expandedTeam]?.includes(agent.id) ? currentTheme.color : 'transparent'
                              }}>
                              {selectedAgents[expandedTeam]?.includes(agent.id) && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div className="flex items-center space-x-3 min-w-0">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 transition-colors ${isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                {agent.name?.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className={`font-bold text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{agent.name}</p>
                                <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Emp: {agent.employeeId || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className={`col-span-full flex flex-col items-center justify-center h-48 border-2 border-dashed rounded-lg transition-all ${isDark ? 'text-gray-500 border-white/5 bg-white/5' : 'text-gray-400 border-gray-100 bg-gray-50'}`}>
                          <p className="font-bold">No agents found matching your search</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6">
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-10 py-3 text-white rounded-lg transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg hover:shadow-teal-900/20 active:scale-95"
          style={{ backgroundColor: currentTheme.color }}
        >
          {loading && <Loader className="w-4 h-4 animate-spin" />}
          <span>Save Floor</span>
        </button>
      </div>
    </div >
  );
}

export default AddFloor;